import { useState, useEffect, useCallback, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Button,
  Input,
  Select,
  Badge,
  DataTable,
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerBody,
  DrawerFooter,
  DrawerClose,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  ConfirmDialog,
  useToast,
  ScoreMiniGauge,
  ScoreDetailsDialog,
  mockScore,
  type DataTableColumn,
} from "@payflow/ui";
import { useAuth } from "../../contexts/AuthContext";
import {
  listClients,
  createClient,
  updateClient,
  deleteClient,
  getClient,
  getClientScore,
  getClientScoresBatch,
  type ClientResponse,
  type ClientScoreResponse,
  type CreateClientData,
  type UpdateClientData,
} from "../../services/clients.service";
import { api } from "../../services/api";
import { BlockedBadge } from "../../components/BlockedBadge";

interface ClientDocument {
  id: string;
  campo: string;
  tipo: string;
  storageKey: string;
  fileUrl?: string;
  status: "pendente" | "aprovado" | "reprovado";
  motivoReprovacao: string | null;
  versao: number;
  createdAt: string;
}

// ============ Masks ============

function maskCpf(value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, 11);
  return digits
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
}

function maskPhone(value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, 11);
  if (digits.length <= 10) {
    return digits
      .replace(/(\d{2})(\d)/, "($1) $2")
      .replace(/(\d{4})(\d)/, "$1-$2");
  }
  return digits
    .replace(/(\d{2})(\d)/, "($1) $2")
    .replace(/(\d{5})(\d)/, "$1-$2");
}

function maskCep(value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, 8);
  return digits.replace(/(\d{5})(\d)/, "$1-$2");
}

function unmask(value: string): string {
  return value.replace(/\D/g, "");
}

// ============ Main Page ============

export function ClientsPage() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [clients, setClients] = useState<ClientResponse[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Delete
  const [deleteTarget, setDeleteTarget] = useState<ClientResponse | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Modal de detalhes do score (mesmo componente da LoansPage / PipelinePage)
  const [scoreTarget, setScoreTarget] = useState<ClientResponse | null>(null);
  const [scoreDetails, setScoreDetails] = useState<ClientScoreResponse | null>(null);
  const [scoreLoading, setScoreLoading] = useState(false);

  // Score real por cliente da lista (batch). key = client.id.
  const [scoreByClient, setScoreByClient] = useState<Record<string, number>>({});

  const canCreate = hasPermission("clientes:criar");
  const canEdit = hasPermission("clientes:editar");
  const canDelete = hasPermission("clientes:excluir");

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const result = await listClients({ page, limit: pageSize, search: search || undefined });
      setClients(result.data);
      setTotal(result.meta.total);
      const ids = result.data.map((r) => r.id);
      if (ids.length > 0) {
        try {
          const scores = await getClientScoresBatch(ids);
          setScoreByClient((prev) => ({ ...prev, ...scores }));
        } catch {
          // silencioso — fallback pro mockScore na render
        }
      }
    } catch {
      toast({ variant: "error", title: t("clients.load_error") });
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, t, toast]);

  useEffect(() => { fetchClients(); }, [fetchClients]);
  useEffect(() => { setPage(1); }, [search]);

  useEffect(() => {
    if (!scoreTarget) return;
    let cancelled = false;
    setScoreDetails(null);
    setScoreLoading(true);
    getClientScore(scoreTarget.id)
      .then((res) => {
        if (cancelled) return;
        setScoreDetails(res);
        setScoreByClient((prev) => ({ ...prev, [scoreTarget.id]: res.score }));
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setScoreLoading(false); });
    return () => { cancelled = true; };
  }, [scoreTarget]);

  const handleNew = () => navigate("/dashboard/clientes/novo");
  const handleEdit = (client: ClientResponse) => navigate(`/dashboard/clientes/${client.id}`);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteClient(deleteTarget.id);
      setDeleteTarget(null);
      toast({ variant: "success", title: t("clients.delete_success") });
      fetchClients();
    } catch (err) {
      toast({ variant: "error", title: err instanceof Error ? err.message : t("clients.delete_error") });
    } finally {
      setDeleting(false);
    }
  };

  const columns: DataTableColumn<ClientResponse>[] = [
    {
      key: "nome",
      header: t("clients.col_name"),
      sortable: true,
      render: (row) => (
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/30">
            <span className="text-sm font-medium text-primary-700 dark:text-primary-300">
              {row.nome.charAt(0).toUpperCase()}
            </span>
          </div>
          <div>
            <div className="flex items-center gap-2">
              <p className="font-medium text-foreground">{row.nome}</p>
              {row.bloqueado && <BlockedBadge />}
            </div>
            {row.profissao && (
              <p className="text-xs text-muted-foreground">{row.profissao}</p>
            )}
          </div>
        </div>
      ),
    },
    {
      key: "cpf",
      header: t("clients.col_cpf"),
      render: (row) => (
        <span className="font-mono text-sm text-muted-foreground">{maskCpf(row.cpf)}</span>
      ),
    },
    {
      key: "telefone",
      header: t("clients.col_phone"),
      render: (row) => (
        <span className="text-sm text-muted-foreground">{maskPhone(row.telefone)}</span>
      ),
    },
    {
      key: "cidade",
      header: t("clients.col_city"),
      render: (row) => (
        <span className="text-sm text-muted-foreground">
          {row.cidade ? `${row.cidade}/${row.estado}` : "-"}
        </span>
      ),
    },
    {
      key: "score",
      header: t("clients.col_score"),
      className: "w-24 text-center",
      render: (row) => (
        <div className="flex justify-center" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            onClick={() => setScoreTarget(row)}
            title={t("loans.score_dialog_title")}
            className="transition-transform hover:scale-105"
          >
            <ScoreMiniGauge score={scoreByClient[row.id] ?? mockScore(row.cpf)} />
          </button>
        </div>
      ),
    },
    {
      key: "ativo",
      header: t("clients.col_status"),
      render: (row) => {
        // Se ha pipelineStatus, ele manda: refletir onde o cliente esta
        // na Linha de Aprovacao. Docs_pendentes = Pendente (amarelo).
        // Recusado = vermelho. Demais status pos-analise = Ativo (verde).
        // Sem pipeline (cadastrado direto no admin) cai em row.ativo puro.
        const ps = row.pipelineStatus;
        if (ps === "docs_pendentes") {
          return <Badge variant="warning" dot size="md">Pendente</Badge>;
        }
        if (ps === "aguardando_reenvio") {
          return <Badge variant="warning" dot size="md">Aguardando Reenvio</Badge>;
        }
        if (ps === "pendente_reanalise") {
          return <Badge variant="warning" dot size="md">Pendente de Reanálise</Badge>;
        }
        if (ps === "recusado") {
          return <Badge variant="error" dot size="md">Recusado</Badge>;
        }
        return (
          <Badge variant={row.ativo ? "success" : "outline"} dot size="md">
            {row.ativo ? t("common.active") : t("common.inactive")}
          </Badge>
        );
      },
    },
    {
      key: "actions",
      header: "",
      className: "w-24 text-right",
      render: (row) => (
        <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
          {canEdit && (
            <Button variant="ghost" size="sm" onClick={() => handleEdit(row)} className="h-8 w-8 p-0">
              <EditIcon />
            </Button>
          )}
          {canDelete && (
            <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(row)} className="h-8 w-8 p-0 text-error hover:text-error">
              <TrashIcon />
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t("clients.title")}</h1>
          <p className="text-sm text-muted-foreground">{t("clients.count", { count: total })}</p>
        </div>
        {canCreate && (
          <Button onClick={handleNew}>
            <PlusIcon />
            {t("clients.new")}
          </Button>
        )}
      </div>

      {/* DataTable */}
      <DataTable
        columns={columns}
        data={clients}
        total={total}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
        loading={loading}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder={t("clients.search_placeholder")}
        emptyMessage={t("clients.empty")}
        onRowClick={canEdit ? handleEdit : undefined}
      />

      {/* Delete Confirm */}
      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={t("clients.delete_title")}
        description={t("clients.delete_message", { name: deleteTarget?.nome })}
        confirmLabel={t("common.delete")}
        cancelLabel={t("common.cancel")}
        variant="danger"
        loading={deleting}
        onConfirm={handleDelete}
      />

      {/* Modal detalhes do score — mesmo componente da LoansPage / PipelinePage */}
      <ScoreDetailsDialog
        open={!!scoreTarget}
        onOpenChange={(o) => { if (!o) { setScoreTarget(null); setScoreDetails(null); } }}
        cpf={scoreTarget?.cpf}
        data={scoreDetails}
        loading={scoreLoading}
        headerSubtitle={scoreTarget ? `${scoreTarget.nome} · ${maskCpf(scoreTarget.cpf)}` : undefined}
        labels={{
          title: t("loans.score_dialog_title"),
          punctualityTitle: t("loans.score_dialog_punctuality"),
          punctualityStats: (paid, total) => t("loans.score_dialog_punctuality_stats", { paid, total }),
          historyTitle: t("loans.score_dialog_history"),
          historyEmpty: t("loans.score_dialog_history_empty"),
          historyLoans: t("loans.score_dialog_history_loans"),
          historyPaidOff: t("loans.score_dialog_history_paid_off"),
          historyPaidInstallments: t("loans.score_dialog_history_paid_installments"),
          historyOverdueInstallments: t("loans.score_dialog_history_overdue_installments"),
          historyBiggestDelay: t("loans.score_dialog_history_biggest_delay"),
          historyBiggestDelayValue: (days) => t("loans.score_dialog_history_biggest_delay_value", { count: days }),
          historyRenegotiations: t("loans.score_dialog_history_renegotiations"),
          historyPartners: t("loans.score_dialog_history_partners"),
          disclaimer: t("loans.score_dialog_disclaimer"),
        }}
      />
    </div>
  );
}

// ============ Client Form (Drawer with Tabs) ============

/**
 * Drawer de criacao/edicao de cliente reutilizavel. Usado inline em
 * ClientsPage e em LoansPage (botao "Info cliente"). Se `clientId` for
 * fornecido, carrega o cliente via API antes de abrir o form.
 */
export function ClientEditDrawer({
  open,
  onOpenChange,
  clientId,
  initialClient,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId?: string | null;
  initialClient?: ClientResponse | null;
  onSaved?: () => void;
}) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [loaded, setLoaded] = useState<ClientResponse | null>(initialClient || null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (initialClient) { setLoaded(initialClient); return; }
    if (!clientId) { setLoaded(null); return; }
    let cancelled = false;
    setLoading(true);
    getClient(clientId)
      .then((c) => { if (!cancelled) setLoaded(c); })
      .catch(() => toast({ variant: "error", title: t("clients.load_error") }))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, clientId, initialClient, t, toast]);

  const handleSave = async (data: CreateClientData | UpdateClientData) => {
    setSaving(true);
    try {
      if (loaded) {
        await updateClient(loaded.id, data as UpdateClientData);
        toast({ variant: "success", title: t("clients.update_success") });
      } else {
        await createClient(data as CreateClientData);
        toast({ variant: "success", title: t("clients.create_success") });
      }
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast({ variant: "error", title: err instanceof Error ? err.message : t("clients.save_error") });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent size="xl">
        <DrawerHeader>
          <DrawerTitle>
            {loaded ? t("clients.edit_title") : t("clients.new_title")}
          </DrawerTitle>
          <DrawerClose onClick={() => onOpenChange(false)} />
        </DrawerHeader>
        {loading ? (
          <div className="flex flex-1 items-center justify-center py-12">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary-200 border-t-primary-600" />
          </div>
        ) : (
          <ClientForm client={loaded} saving={saving} onSave={handleSave} />
        )}
      </DrawerContent>
    </Drawer>
  );
}

interface ClientFormProps {
  client: ClientResponse | null;
  saving: boolean;
  onSave: (data: CreateClientData | UpdateClientData) => void;
  /**
   * "drawer" (default): usa DrawerBody/DrawerFooter — mantido pra
   * ClientEditDrawer usado em LoansPage.
   * "page": usa divs neutras — usado em /dashboard/clientes/:id.
   */
  variant?: "drawer" | "page";
}

export function ClientForm({ client, saving, onSave, variant = "drawer" }: ClientFormProps) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("pessoal");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState({
    nome: client?.nome || "",
    cpf: client?.cpf ? maskCpf(client.cpf) : "",
    telefone: client?.telefone ? maskPhone(client.telefone) : "",
    whatsapp: client?.whatsapp ? maskPhone(client.whatsapp) : "",
    dataNascimento: client?.dataNascimento || "",
    rg: client?.rg || "",
    nacionalidade: client?.nacionalidade || "",
    estadoCivil: client?.estadoCivil || "",
    profissao: client?.profissao || "",
    cep: client?.cep ? maskCep(client.cep) : "",
    endereco: client?.endereco || "",
    numero: client?.numero || "",
    complemento: client?.complemento || "",
    bairro: client?.bairro || "",
    cidade: client?.cidade || "",
    estado: client?.estado || "",
    chavePix: client?.chavePix || "",
    tipoChavePix: client?.tipoChavePix || "",
    serasa: client?.serasa || false,
  });

  // Referencias (contatos de emergencia) - array mutavel
  const [referencias, setReferencias] = useState<Array<{ nome: string; telefone: string; relacao: string }>>(
    () =>
      (client?.contatosEmergencia || []).map((r) => ({
        nome: r.nome || "",
        telefone: r.telefone ? maskPhone(r.telefone) : "",
        relacao: r.relacao || "",
      }))
  );

  const addReferencia = () => {
    setReferencias((prev) => [...prev, { nome: "", telefone: "", relacao: "" }]);
  };

  const updateReferencia = (index: number, field: "nome" | "telefone" | "relacao", value: string) => {
    setReferencias((prev) =>
      prev.map((r, i) =>
        i === index ? { ...r, [field]: field === "telefone" ? maskPhone(value) : value } : r
      )
    );
  };

  const removeReferencia = (index: number) => {
    setReferencias((prev) => prev.filter((_, i) => i !== index));
  };

  const set = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validate = () => {
    const e: Record<string, string> = {};
    if (!formData.nome.trim()) e.nome = t("clients.validate_name");
    if (unmask(formData.cpf).length !== 11) e.cpf = t("clients.validate_cpf");
    if (unmask(formData.telefone).length < 10) e.telefone = t("clients.validate_phone");
    setErrors(e);
    if (Object.keys(e).length > 0) {
      // Switch to tab with error
      if (e.nome || e.cpf || e.telefone) setActiveTab("pessoal");
    }
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    const refsClean = referencias
      .filter((r) => r.nome.trim() && unmask(r.telefone).length >= 10)
      .map((r) => ({
        nome: r.nome.trim(),
        telefone: unmask(r.telefone),
        ...(r.relacao.trim() ? { relacao: r.relacao.trim() } : {}),
      }));

    onSave({
      nome: formData.nome,
      cpf: unmask(formData.cpf),
      telefone: unmask(formData.telefone),
      whatsapp: unmask(formData.whatsapp) || null,
      dataNascimento: formData.dataNascimento || undefined,
      rg: formData.rg || undefined,
      nacionalidade: formData.nacionalidade || undefined,
      estadoCivil: formData.estadoCivil || undefined,
      profissao: formData.profissao || undefined,
      cep: unmask(formData.cep) || undefined,
      endereco: formData.endereco || undefined,
      numero: formData.numero || undefined,
      complemento: formData.complemento || undefined,
      bairro: formData.bairro || undefined,
      cidade: formData.cidade || undefined,
      estado: formData.estado || undefined,
      chavePix: formData.chavePix || undefined,
      tipoChavePix: formData.tipoChavePix || undefined,
      contatosEmergencia: refsClean,
      serasa: formData.serasa,
    });
  };

  const estadosCivis = [
    { value: "", label: t("clients.marital_select") },
    { value: "solteiro", label: t("clients.marital_single") },
    { value: "casado", label: t("clients.marital_married") },
    { value: "divorciado", label: t("clients.marital_divorced") },
    { value: "viuvo", label: t("clients.marital_widowed") },
    { value: "uniao_estavel", label: t("clients.marital_stable_union") },
  ];

  const tiposChavePix = [
    { value: "", label: t("clients.pix_select") },
    { value: "cpf", label: t("clients.pix_cpf") },
    { value: "telefone", label: t("clients.pix_phone") },
    { value: "email", label: t("clients.pix_email") },
    { value: "aleatoria", label: t("clients.pix_random") },
  ];

  const isPage = variant === "page";
  const Body = isPage
    ? ({ children }: { children: ReactNode }) => (
        <div className="min-h-0 flex-1 overflow-y-auto px-1 py-4">{children}</div>
      )
    : DrawerBody;
  const Footer = isPage
    ? ({ children }: { children: ReactNode }) => (
        <div className="flex justify-end gap-2 border-t border-border pt-4">{children}</div>
      )
    : DrawerFooter;

  return (
    <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
      <Body>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full">
            <TabsTrigger value="pessoal" className="flex-1">
              {t("clients.tab_personal")}
              {(errors.nome || errors.cpf || errors.telefone) && (
                <span className="ml-1.5 h-1.5 w-1.5 rounded-full bg-error" />
              )}
            </TabsTrigger>
            <TabsTrigger value="endereco" className="flex-1">
              {t("clients.tab_address")}
            </TabsTrigger>
            <TabsTrigger value="pix" className="flex-1">
              {t("clients.tab_pix")}
            </TabsTrigger>
            <TabsTrigger value="referencias" className="flex-1">
              {t("clients.tab_references")}
            </TabsTrigger>
            {/*
             * Tab "Cadastro app": aparece somente quando o cliente foi
             * cadastrado via app (tem dadosOnboarding). Mostra TUDO que veio
             * do onboarding — inclui campos custom que nao tem coluna fixa.
             */}
            {client?.id && (
              <TabsTrigger value="documentos" className="flex-1">
                {t("clients.tab_documents")}
              </TabsTrigger>
            )}
            {client?.dadosOnboarding && (
              <TabsTrigger value="cadastro_app" className="flex-1">
                {t("clients.tab_app_data")}
              </TabsTrigger>
            )}
          </TabsList>

          {/* Tab: Pessoal */}
          <TabsContent value="pessoal">
            <div className="space-y-4">
              <Input
                label={t("clients.label_full_name")}
                value={formData.nome}
                onChange={(e) => set("nome", e.target.value)}
                error={errors.nome}
                required
              />
              <div className="grid gap-4 sm:grid-cols-2">
                <Input
                  label={t("clients.label_cpf")}
                  value={formData.cpf}
                  onChange={(e) => set("cpf", maskCpf(e.target.value))}
                  error={errors.cpf}
                  placeholder="000.000.000-00"
                  required
                />
                <PhoneInputWithWA
                  label={t("clients.label_phone")}
                  value={formData.telefone}
                  onChange={(v) => set("telefone", maskPhone(v))}
                  error={errors.telefone}
                  required
                />
              </div>
              <PhoneInputWithWA
                label={t("clients.label_whatsapp")}
                value={formData.whatsapp}
                onChange={(v) => set("whatsapp", maskPhone(v))}
              />
              <div className="grid gap-4 sm:grid-cols-2">
                <Input
                  label={t("clients.label_birth_date")}
                  type="date"
                  value={formData.dataNascimento}
                  onChange={(e) => set("dataNascimento", e.target.value)}
                />
                <Input
                  label={t("clients.label_rg")}
                  value={formData.rg}
                  onChange={(e) => set("rg", e.target.value)}
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <Select
                  label={t("clients.label_marital_status")}
                  value={formData.estadoCivil}
                  onChange={(e) => set("estadoCivil", e.target.value)}
                  options={estadosCivis}
                />
                <Input
                  label={t("clients.label_profession")}
                  value={formData.profissao}
                  onChange={(e) => set("profissao", e.target.value)}
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="serasa"
                  checked={formData.serasa}
                  onChange={(e) => set("serasa", e.target.checked)}
                  className="h-4 w-4 rounded border-input text-primary-600 focus:ring-primary-500"
                />
                <label htmlFor="serasa" className="text-sm text-muted-foreground">
                  {t("clients.label_serasa")}
                </label>
              </div>
            </div>
          </TabsContent>

          {/* Tab: Endereco */}
          <TabsContent value="endereco">
            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-3">
                <Input
                  label={t("clients.label_cep")}
                  value={formData.cep}
                  onChange={(e) => set("cep", maskCep(e.target.value))}
                  placeholder="00000-000"
                />
                <div className="sm:col-span-2">
                  <Input
                    label={t("clients.label_address")}
                    value={formData.endereco}
                    onChange={(e) => set("endereco", e.target.value)}
                  />
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-3">
                <Input
                  label={t("clients.label_number")}
                  value={formData.numero}
                  onChange={(e) => set("numero", e.target.value)}
                />
                <div className="sm:col-span-2">
                  <Input
                    label={t("clients.label_complement")}
                    value={formData.complemento}
                    onChange={(e) => set("complemento", e.target.value)}
                  />
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-3">
                <Input
                  label={t("clients.label_neighborhood")}
                  value={formData.bairro}
                  onChange={(e) => set("bairro", e.target.value)}
                />
                <Input
                  label={t("clients.label_city")}
                  value={formData.cidade}
                  onChange={(e) => set("cidade", e.target.value)}
                />
                <Input
                  label={t("clients.label_state")}
                  value={formData.estado}
                  onChange={(e) => set("estado", e.target.value.toUpperCase().slice(0, 2))}
                  placeholder="SP"
                  maxLength={2}
                />
              </div>
            </div>
          </TabsContent>

          {/* Tab: PIX */}
          <TabsContent value="pix">
            <div className="space-y-4">
              <Select
                label={t("clients.label_pix_type")}
                value={formData.tipoChavePix}
                onChange={(e) => set("tipoChavePix", e.target.value)}
                options={tiposChavePix}
              />
              <Input
                label={t("clients.label_pix_key")}
                value={formData.chavePix}
                onChange={(e) => set("chavePix", e.target.value)}
                placeholder={
                  formData.tipoChavePix === "cpf" ? "00000000000" :
                  formData.tipoChavePix === "telefone" ? "+5500000000000" :
                  formData.tipoChavePix === "email" ? "email@exemplo.com" :
                  t("clients.placeholder_random_pix")
                }
              />
            </div>
          </TabsContent>

          {/* Tab: Referencias */}
          <TabsContent value="referencias">
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                {t("clients.references_hint")}
              </p>
              {referencias.length === 0 && (
                <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
                  {t("clients.references_empty")}
                </div>
              )}
              {referencias.map((ref, i) => (
                <div
                  key={i}
                  className="space-y-3 rounded-lg border border-border bg-muted/30 p-3"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-muted-foreground">
                      {t("clients.references_item")} #{i + 1}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeReferencia(i)}
                      className="text-xs font-medium text-red-600 hover:text-red-700"
                    >
                      {t("common.remove")}
                    </button>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Input
                      label={t("clients.references_name")}
                      value={ref.nome}
                      onChange={(e) => updateReferencia(i, "nome", e.target.value)}
                    />
                    <PhoneInputWithWA
                      label={t("clients.references_phone")}
                      value={ref.telefone}
                      onChange={(v) => updateReferencia(i, "telefone", v)}
                    />
                  </div>
                  <Input
                    label={t("clients.references_relation")}
                    value={ref.relacao}
                    onChange={(e) => updateReferencia(i, "relacao", e.target.value)}
                    placeholder={t("clients.references_relation_placeholder")}
                  />
                </div>
              ))}
              <Button type="button" variant="outline" onClick={addReferencia} className="w-full">
                + {t("clients.references_add")}
              </Button>
            </div>
          </TabsContent>

          {/* Tab: Documentos — thumbnails read-only. Acoes de aprovar/reprovar
              ficam no /dashboard/pipeline; aqui e so visualizacao. */}
          {client?.id && (
            <TabsContent value="documentos">
              <ClientDocumentsPanel clienteId={client.id} />
            </TabsContent>
          )}

          {/* Tab: Cadastro app — snapshot read-only das respostas do onboarding */}
          {client?.dadosOnboarding && (
            <TabsContent value="cadastro_app">
              <OnboardingDataPanel
                data={client.dadosOnboarding}
                clienteId={client.id}
              />
            </TabsContent>
          )}
        </Tabs>
      </Body>

      <Footer>
        <Button type="submit" loading={saving}>
          {client ? t("common.save") : t("common.register")}
        </Button>
      </Footer>
    </form>
  );
}

// ============ Onboarding Data Panel ============

/**
 * Visualizador read-only do `dadosOnboarding` salvo na conversao do lead em
 * cliente. Mostra TUDO o que o cliente respondeu — incluindo campos custom
 * do FlowEditor que nao tem coluna fixa em `clientes` (conditional_input,
 * multi-field email/telefone, video URIs, etc).
 *
 * Renderiza chave/valor com formatacao adequada por tipo: objetos viram lista
 * indentada, arrays viram bullets, strings ficam como texto, URIs viram link.
 */
function OnboardingDataPanel({
  data,
  clienteId,
}: {
  data: Record<string, unknown>;
  clienteId?: string;
}) {
  const { t } = useTranslation();
  // LGPD: nunca expor senha/campos sensiveis no snapshot. Filtra ANTES de
  // renderizar — o hash vive em client_credentials, texto plano nao deveria
  // nem chegar aqui (backend tambem filtra desde o commit atual), mas
  // defendemos no cliente pra registros antigos.
  const SENSITIVE_KEYS = new Set(["senha", "password", "confirmar_senha", "senha_confirmacao"]);
  // Campos tecnicos internos que nao interessam ao usuario. _flowMeta e o
  // snapshot da estrutura do flow (usado no backend pra rotular/agrupar).
  const HIDDEN_KEYS = new Set(["loan_plan_id", "_flowmeta"]);
  const entries = Object.entries(data).filter(
    ([k, v]) =>
      !SENSITIVE_KEYS.has(k.toLowerCase()) &&
      !HIDDEN_KEYS.has(k.toLowerCase()) &&
      v !== null &&
      v !== undefined &&
      v !== "",
  );
  const [docsByCampo, setDocsByCampo] = useState<Record<string, ClientDocument>>({});
  const [fieldMeta, setFieldMeta] = useState<Record<string, FieldMeta>>({});

  useEffect(() => {
    if (!clienteId) return;
    let cancelled = false;
    (async () => {
      try {
        const [docsResp, labelsResp] = await Promise.all([
          api.get<ClientDocument[]>(`/documents?cliente_id=${clienteId}&limit=100`),
          api.get<Record<string, FieldMeta>>(`/clients/${clienteId}/onboarding-labels`),
        ]);
        if (cancelled) return;
        const map: Record<string, ClientDocument> = {};
        (docsResp.data || []).forEach((d) => {
          map[d.campo] = d;
        });
        setDocsByCampo(map);
        setFieldMeta(labelsResp.data || {});
      } catch {
        // silencioso — se falhar cai no rendering plano
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clienteId]);

  if (entries.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
        {t("clients.app_data_empty")}
      </div>
    );
  }

  // 1) Ordena entries pela ordem do step no flow (via meta.order). Campos
  //    sem meta (custom, legados) vao pro final na ordem original de insercao.
  const sortedEntries = [...entries]
    .map(([k, v], i) => ({ k, v, order: fieldMeta[k]?.order ?? 999 + i }))
    .sort((a, b) => a.order - b.order);

  // 2) Reduz em blocos: consecutivos com mesmo groupId viram 1 card composto.
  //    Sem groupId, cada entry vira 1 card individual.
  type Block =
    | { kind: "single"; key: string; value: unknown }
    | { kind: "group"; groupId: string; groupLabel: string; items: { key: string; value: unknown }[] };
  const blocks: Block[] = [];
  for (const { k, v } of sortedEntries) {
    const gid = fieldMeta[k]?.groupId;
    const glabel = fieldMeta[k]?.groupLabel || "";
    const last = blocks[blocks.length - 1];
    if (gid && last?.kind === "group" && last.groupId === gid) {
      last.items.push({ key: k, value: v });
    } else if (gid) {
      blocks.push({ kind: "group", groupId: gid, groupLabel: glabel, items: [{ key: k, value: v }] });
    } else {
      blocks.push({ kind: "single", key: k, value: v });
    }
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        {t("clients.app_data_hint")}
      </p>
      {blocks.map((block, i) =>
        block.kind === "single" ? (
          <OnboardingField
            key={block.key}
            fieldKey={block.key}
            value={block.value}
            doc={docsByCampo[block.key]}
            overrideLabel={fieldMeta[block.key]?.label}
          />
        ) : (
          <OnboardingGroupCard
            key={`grp-${i}-${block.groupId}`}
            groupLabel={block.groupLabel}
            items={block.items}
            docsByCampo={docsByCampo}
            fieldMeta={fieldMeta}
          />
        ),
      )}
    </div>
  );
}

interface FieldMeta {
  label: string;
  order: number;
  groupId?: string;
  groupLabel?: string;
}

/**
 * Card composto: renderiza N sub-fields sob um titulo comum (o label do
 * step pai, ex: "DOCUMENTO"). Ideal pra document_upload com fields[].
 */
function OnboardingGroupCard({
  groupLabel,
  items,
  docsByCampo,
  fieldMeta,
}: {
  groupLabel: string;
  items: { key: string; value: unknown }[];
  docsByCampo: Record<string, ClientDocument>;
  fieldMeta: Record<string, FieldMeta>;
}) {
  return (
    <div className="rounded-lg border border-border bg-muted/30 p-3">
      {groupLabel && (
        <div className="mb-2 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
          {groupLabel}
        </div>
      )}
      <div className="space-y-2">
        {items.map(({ key, value }) => {
          const doc = docsByCampo[key];
          const subLabel = fieldMeta[key]?.label || humanizeKey(key);
          const showThumbnail =
            doc &&
            typeof value === "string" &&
            (value.startsWith("file:") || value.startsWith("data:") || value === doc.storageKey);
          return (
            <div key={key} className="rounded-md border border-border bg-card p-2">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                {subLabel}
              </div>
              <div className="mt-1 break-words text-sm text-foreground">
                {showThumbnail ? <DocPreview doc={doc!} /> : <RenderValue value={value} />}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function OnboardingField({
  fieldKey,
  value,
  doc,
  overrideLabel,
}: {
  fieldKey: string;
  value: unknown;
  doc?: ClientDocument;
  overrideLabel?: string;
}) {
  // Preferencia: label do FlowEditor (o texto que o cliente viu) > fallback
  // humanizado a partir do nome tecnico do campo.
  const label = overrideLabel || humanizeKey(fieldKey);
  // Se o campo tem um doc no client_documents, mostra thumbnail em vez do
  // valor cru (que costuma ser um file:// URI local do celular, inutil).
  const showThumbnail =
    doc &&
    typeof value === "string" &&
    (value.startsWith("file:") || value.startsWith("data:") || value === doc.storageKey);
  const formatted = showThumbnail ? null : formatByField(fieldKey, value);
  return (
    <div className="rounded-lg border border-border bg-muted/30 p-3">
      <div className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="mt-1 break-words text-sm text-foreground">
        {showThumbnail ? (
          <DocPreview doc={doc!} />
        ) : formatted !== null ? (
          <span>{formatted}</span>
        ) : (
          <RenderValue value={value} />
        )}
      </div>
    </div>
  );
}

/**
 * Formata valor por heuristica no nome do campo: monetario (R$),
 * porcentagem (%), CPF, telefone, senha (mascarada). Retorna null quando
 * nao aplicavel — cai no rendering generico.
 */
function formatByField(key: string, value: unknown): string | null {
  if (value === null || value === undefined || value === "") return null;
  const k = key.toLowerCase();

  const asNumber =
    typeof value === "number"
      ? value
      : typeof value === "string"
        ? Number(value.replace(",", "."))
        : NaN;

  // Monetario: valor_emprestimo, valor_parcela, valor_total, montante...
  if (/(^|_)(valor|montante|preco|amount)($|_)/.test(k) && Number.isFinite(asNumber)) {
    return asNumber.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  }

  // Porcentagem: taxa_juros, juros, percentual...
  if (/(^|_)(taxa|juros|percentual|percent)($|_)/.test(k) && Number.isFinite(asNumber)) {
    return `${asNumber.toLocaleString("pt-BR", { maximumFractionDigits: 2 })}%`;
  }

  if (typeof value !== "string") return null;

  // CPF: 11 digitos → 000.000.000-00
  if (k === "cpf") {
    const d = value.replace(/\D/g, "");
    if (d.length === 11) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  }

  // Telefone / WhatsApp: 10-11 digitos → (00) 00000-0000
  if (k === "whatsapp" || k.includes("telefone") || k.includes("celular")) {
    const d = value.replace(/\D/g, "");
    if (d.length === 11) return d.replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3");
    if (d.length === 10) return d.replace(/(\d{2})(\d{4})(\d{4})/, "($1) $2-$3");
  }

  return null;
}

function DocPreview({ doc }: { doc: ClientDocument }) {
  const url = doc.fileUrl || "#";
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="inline-block overflow-hidden rounded-md border border-border transition-opacity hover:opacity-80"
    >
      {doc.tipo === "image" ? (
        <img src={url} alt={doc.campo} className="max-h-40 w-auto object-contain" />
      ) : doc.tipo === "video" ? (
        <div className="flex h-24 w-32 items-center justify-center bg-slate-800 text-2xl text-white">
          ▶
        </div>
      ) : (
        <div className="flex h-24 w-32 items-center justify-center bg-muted text-xs text-muted-foreground">
          PDF
        </div>
      )}
    </a>
  );
}

function RenderValue({ value }: { value: unknown }) {
  if (value === null || value === undefined) return <span className="text-muted-foreground">—</span>;
  if (typeof value === "string") {
    if (/^https?:\/\//.test(value)) {
      return (
        <a
          href={value}
          target="_blank"
          rel="noreferrer"
          className="text-primary-600 underline hover:text-primary-700"
        >
          {value}
        </a>
      );
    }
    return <span className="whitespace-pre-wrap">{value}</span>;
  }
  if (typeof value === "boolean") return <span>{value ? "Sim" : "Não"}</span>;
  if (typeof value === "number") return <span>{value}</span>;
  if (Array.isArray(value)) {
    return (
      <ul className="list-disc space-y-1 pl-4">
        {value.map((item, i) => (
          <li key={i}>
            <RenderValue value={item} />
          </li>
        ))}
      </ul>
    );
  }
  if (typeof value === "object") {
    return (
      <div className="space-y-1">
        {Object.entries(value as Record<string, unknown>).map(([k, v]) => (
          <div key={k} className="flex flex-wrap gap-x-2">
            <span className="text-xs font-medium text-muted-foreground">
              {humanizeKey(k)}:
            </span>
            <span className="text-sm">
              <RenderValue value={v} />
            </span>
          </div>
        ))}
      </div>
    );
  }
  return <span>{String(value)}</span>;
}

/**
 * Converte snake_case/camelCase pra texto legivel ("nome_completo" -> "Nome
 * Completo"). Mantem a fonte verdadeira no JSON sem precisar mapear chave a
 * chave — qualquer field custom do FlowEditor ja sai bem formatado.
 */
function humanizeKey(key: string): string {
  return key
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// ============ Client Documents Panel ============

/**
 * Lista read-only dos documentos do cliente. Fetch em /v1/admin/documents
 * filtrado por cliente_id. Nao expoe acoes de aprovar/reprovar — isso mora
 * no /dashboard/pipeline. Aqui e so pra visualizar o que o cliente subiu.
 */
function ClientDocumentsPanel({ clienteId }: { clienteId: string }) {
  const { t } = useTranslation();
  const [docs, setDocs] = useState<ClientDocument[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const resp = await api.get<ClientDocument[]>(`/documents?cliente_id=${clienteId}&limit=100`);
        if (!cancelled) setDocs(resp.data || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clienteId]);

  const getFileUrl = (doc: ClientDocument) => {
    return doc.fileUrl || "#";
  };

  if (loading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
      </div>
    );
  }

  if (docs.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
        {t("clients.documents_empty")}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {docs.map((doc) => {
        const statusColor = doc.status === "aprovado"
          ? "bg-green-100 text-green-700 border-green-300"
          : doc.status === "reprovado"
            ? "bg-red-100 text-red-700 border-red-300"
            : "bg-yellow-100 text-yellow-700 border-yellow-300";
        const statusLabel = doc.status === "pendente" && doc.versao > 1
          ? t("clients.documents_status_reanalise")
          : doc.status;
        return (
          <div key={doc.id} className="rounded-lg border border-border bg-card p-3">
            <div className="flex gap-3">
              <a
                href={getFileUrl(doc)}
                target="_blank"
                rel="noreferrer"
                className="h-20 w-20 shrink-0 overflow-hidden rounded-md border border-border bg-muted transition-opacity hover:opacity-80"
              >
                {doc.tipo === "image" ? (
                  <img
                    src={getFileUrl(doc)}
                    alt={doc.campo}
                    className="h-full w-full object-cover"
                  />
                ) : doc.tipo === "video" ? (
                  <div className="flex h-full w-full items-center justify-center bg-slate-800 text-2xl text-white">
                    ▶
                  </div>
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-xs text-muted-foreground">
                    PDF
                  </div>
                )}
              </a>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-foreground">
                  {humanizeKey(doc.campo)}
                </p>
                <span className={`mt-1 inline-flex rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase ${statusColor}`}>
                  {statusLabel}
                </span>
                {doc.motivoReprovacao && (
                  <p className="mt-1 truncate text-xs text-red-600" title={doc.motivoReprovacao}>
                    {doc.motivoReprovacao}
                  </p>
                )}
                {doc.versao > 1 && (
                  <p className="mt-0.5 text-[10px] text-muted-foreground">
                    {t("clients.documents_version", { n: doc.versao })}
                  </p>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ============ Phone Input com botao WhatsApp ============

/**
 * Wrapper do Input que adiciona um botao WhatsApp na direita. Habilita
 * quando o valor tem >= 10 digitos. Prefixa 55 (BR) se nao ha country
 * code. Usado nos campos Telefone / WhatsApp / Referencias do editar.
 */
function PhoneInputWithWA({
  label,
  value,
  onChange,
  error,
  required,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  error?: string;
  required?: boolean;
}) {
  const digits = value.replace(/\D/g, "");
  const enabled = digits.length >= 10;
  const withCountry = digits.length <= 11 ? `55${digits}` : digits;
  return (
    <div className="flex items-end gap-2">
      <div className="flex-1">
        <Input
          label={label}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          error={error}
          placeholder="(00) 00000-0000"
          required={required}
        />
      </div>
      <a
        href={enabled ? `https://wa.me/${withCountry}` : undefined}
        target={enabled ? "_blank" : undefined}
        rel="noreferrer"
        className={`mb-[2px] flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border transition-colors ${
          enabled
            ? "border-green-600 bg-green-50 text-green-600 hover:bg-green-100"
            : "border-border bg-muted text-muted-foreground opacity-50 pointer-events-none"
        }`}
        title={enabled ? "Abrir no WhatsApp" : "Digite um telefone valido"}
        aria-label="Abrir no WhatsApp"
      >
        <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.966-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
      </a>
    </div>
  );
}

// ============ Icons ============

function PlusIcon() {
  return (
    <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
  );
}

function EditIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  );
}
